import { SORT_ORDER } from 'config/constants';
import { SortOrder, SortColumn, WorklistItemStatus, CriticalResultStatus } from 'generated/graphql';
import { DEFAULT_SORT_COLUMNS, DEFAULT_SORT_ORDERS, RELATIVE_DATE_REGEX } from './constants';
import {
  FilterDefinitionType,
  FilterStateType,
  FilterVariant,
  NumberRangeTupleString,
  ProcessedBooleanFilterValueType,
  ProcessedFilterStateType,
  ProcessedNumberRangeFilterValueType,
  WorklistFiltersType,
} from './types';
import { WorklistSurface } from '../Worklist/types';
import type { WorklistSurfaceType } from '../Worklist/types';
import { endOfDay } from 'date-fns/endOfDay';
import { isValid } from 'date-fns/isValid';
import { CUSTOM_WORKLIST_FILTERS_IN_FLIGHT_KEY } from '../Worklist/WorklistPageV2/constants';

const VALID_COLUMN_SET = new Set(Object.values(SortColumn));
const VALID_ORDER_SET = new Set([SORT_ORDER.ASCENDING, SORT_ORDER.DESCENDING]);

export const validateSurface = (
  surface: string,
  defaultSurface: WorklistSurfaceType = WorklistSurface.CUSTOM_WORKLIST
): WorklistSurfaceType => {
  return (
    Object.values(WorklistSurface).find((s: WorklistSurfaceType) => s === surface) ??
    defaultSurface ??
    WorklistSurface.CUSTOM_WORKLIST
  );
};

export const validateSortColumns = (
  columns: string[],
  defaultColumn: Array<SortColumn> = DEFAULT_SORT_COLUMNS
): Array<SortColumn> => {
  const validColumns = columns.filter((column) =>
    VALID_COLUMN_SET.has(column as SortColumn)
  ) as Array<SortColumn>;
  return validColumns.length > 0 ? validColumns : defaultColumn;
};

export const validateSortOrders = (
  orders: string[],
  defaultOrders: Array<SortOrder> = DEFAULT_SORT_ORDERS
): Array<SortOrder> => {
  const validOrders = orders.filter((order) =>
    VALID_ORDER_SET.has(order as SortOrder)
  ) as Array<SortOrder>;
  return validOrders.length > 0 ? validOrders : defaultOrders;
};

type WorklistURLParams = {
  surface: null | WorklistSurfaceType;
  sortColumn: Array<SortColumn>;
  sortOrder: Array<SortOrder>;
  filters: Nullable<Partial<Record<WorklistFiltersType, string>>>;
};

export const getWorklistURLParams = (searchParams: URLSearchParams): WorklistURLParams => {
  const params: WorklistURLParams = {
    sortColumn: DEFAULT_SORT_COLUMNS,
    sortOrder: DEFAULT_SORT_ORDERS,
    surface: null,
    filters: null,
  };

  for (const [key, value] of searchParams) {
    switch (key) {
      // If sortColumn and sortOrder are not the same length, the values will be
      // corrected by tanstack table
      case 'sortColumn':
        params.sortColumn = validateSortColumns(value.split(','));
        break;
      case 'sortOrder':
        params.sortOrder = validateSortOrders(value.split(','));
        break;
      case 'tab':
        params.surface = validateSurface(value);
        break;
      default:
        if (key.startsWith('filters')) {
          params.filters = params.filters ?? {};
          const filterKey = key.match(/filters\[([^\]]*)]/)?.[1];
          if (params.filters != null && filterKey != null) {
            params.filters[filterKey as WorklistFiltersType] = value;
          }
        }
        break;
    }
  }

  return params;
};

// TODO: @kcwijaya figure out how to type this function well. If we make the
// return type not as strict, it messes up things for the future mutation
export const processFiltersForQuery = (
  filters: FilterStateType,
  definitions: FilterDefinitionType
): ProcessedFilterStateType => {
  const processedFilters: ProcessedFilterStateType = {};

  for (const [key, value] of Object.entries(filters)) {
    const filterKey = key as WorklistFiltersType;
    const filter = definitions[filterKey];
    if (filter == null) {
      continue;
    }

    if (filterKey === CUSTOM_WORKLIST_FILTERS_IN_FLIGHT_KEY) {
      // This is a special key used to track if custom worklist filters are being processed
      // It should not be included in the processed filters
      continue;
    }

    switch (filter.type) {
      case FilterVariant.MultiSelect:
        if (filter.isBoolean === true) {
          processedFilters[filterKey] = processBooleanFilter(value);
        } else {
          processedFilters[filterKey] = value;
        }
        break;
      case FilterVariant.DateRange:
        processedFilters[filterKey] = processDateFilter(value);
        break;
      case FilterVariant.Toggle:
        processedFilters[filterKey] = typeof value === 'boolean' ? value : value[0] === 'true';
        break;
      case FilterVariant.NumberRange:
        processedFilters[filterKey] = processNumberRangeFilter(value as NumberRangeTupleString);
        break;
      case FilterVariant.Text:
        processedFilters[filterKey] = value[0] ?? '';
        break;
      default:
        break;
    }
  }

  return processedFilters;
};

export const processBooleanFilter = (values: Array<string>): ProcessedBooleanFilterValueType => {
  if (values.length === 1) {
    return values[0] === 'true';
  }

  return null;
};

export const processDateFilter = (
  values: Array<Nullable<string>>
): Array<string> | [Nullable<Date>, Nullable<Date>] => {
  if (values.length === 0) {
    return [null, null];
  }

  let processedDates: Array<never> | Array<null | Date> = [];

  if (values.length === 1) {
    if (isRelativeDate(values[0])) {
      return [values[0]] as Array<string>;
    } else {
      processedDates = [values[0] != null ? new Date(values[0]) : null, null];
    }
  } else {
    processedDates = [
      values[0] != null ? new Date(values[0]) : null,
      values[1] != null ? endOfDay(new Date(values[1])) : null,
    ];
  }

  return [
    processedDates[0]?.getTime() == null || isNaN(processedDates[0].getTime())
      ? null
      : processedDates[0],
    processedDates[1]?.getTime() == null || isNaN(processedDates[1].getTime())
      ? null
      : processedDates[1],
  ];
};

export const processNumberRangeFilter = (
  values: NumberRangeTupleString | Array<Nullable<string>>
): ProcessedNumberRangeFilterValueType => {
  if (values.length === 0) {
    return [null, null];
  }

  const processValue = (value?: string | null): Nullable<number> => {
    if (value == null || value === '') {
      return null;
    }
    const parsed = parseInt(value);
    return isNaN(parsed) ? null : parsed;
  };

  if (values.length === 1) {
    return [processValue(values[0]), null];
  }

  return [processValue(values[0]), processValue(values[1])];
};

export const ageRangeToDOBRange = (
  ageRange: ProcessedNumberRangeFilterValueType
): [Nullable<Date>, Nullable<Date>] => {
  const [minAge, maxAge] = ageRange;
  const today = new Date();

  let earliestDOB: Nullable<Date> = null;
  let latestDOB: Nullable<Date> = null;

  /*
   * Age range of [minAge, maxAge] maps to
   *   earliestDOB = (maxAge + 1 years ago) + 1 day
   *   latestDOB   = (minAge years ago)
   *
   * Example with the date used for the unit tests = 2024‑11‑18 and [30, 30]:
   *   earliestDOB = 1993‑11‑19 (turn 31 tomorrow)
   *   latestDOB   = 1994‑11‑18 (just turned 30 today)
   *
   */

  if (maxAge != null) {
    earliestDOB = new Date(today);
    earliestDOB.setFullYear(today.getFullYear() - maxAge - 1);
    earliestDOB.setDate(earliestDOB.getDate() + 1);
  }

  if (minAge != null) {
    latestDOB = new Date(today);
    latestDOB.setFullYear(today.getFullYear() - minAge);
  }

  return [earliestDOB, latestDOB];
};

export const isRelativeDate = (date: Nullable<string>): boolean => {
  if (date == null || typeof date !== 'string') {
    return false;
  }

  return date.match(RELATIVE_DATE_REGEX) != null;
};

export const parseFiltersFromURLParams = (
  urlFiltersToApply: Partial<Record<WorklistFiltersType, string>>,
  surfaceFilters?: FilterDefinitionType
): Partial<Record<WorklistFiltersType, string[]>> => {
  const filters: FilterStateType = {};

  Object.keys(urlFiltersToApply).forEach((key) => {
    const filterKey = key as WorklistFiltersType;
    const urlFilter = urlFiltersToApply[filterKey];
    const initialFilter = surfaceFilters?.[filterKey];

    if (initialFilter == null || urlFilter == null) {
      return;
    }

    switch (initialFilter.type) {
      case FilterVariant.MultiSelect:
      case FilterVariant.NumberRange:
        filters[filterKey] = urlFilter.split(',');
        break;
      case FilterVariant.DateRange:
        filters[filterKey] = urlFilter.split(',').map((value, index) => {
          if (isRelativeDate(value)) {
            return value;
          }

          const date = new Date(value);
          if (isNaN(date.getTime())) {
            return '';
          }

          return (index === 1 ? endOfDay(date) : date).toLocaleString();
        });
        break;
      case FilterVariant.Toggle:
        filters[filterKey] = [urlFilter];
        break;
      case FilterVariant.Text:
        filters[filterKey] = [urlFilter];
        break;
      default:
        break;
    }
  });

  return filters;
};

export const getWorklistStatusFilterItems = (hasQCPermission: boolean) => {
  const baseStatuses = [
    { value: WorklistItemStatus.Archive, label: 'Archive' },
    { value: WorklistItemStatus.InProgress, label: 'In Progress' },
    { value: WorklistItemStatus.InProgressAddendum, label: 'In Progress Addendum' },
    { value: WorklistItemStatus.Pending, label: 'Pending' },
  ];

  if (!hasQCPermission) {
    return baseStatuses;
  }

  return baseStatuses
    .concat([
      { value: WorklistItemStatus.Qc, label: 'QC' },
      { value: WorklistItemStatus.Mismatched, label: 'Mismatched' },
      { value: WorklistItemStatus.InTransit, label: 'In Transit' },
      { value: WorklistItemStatus.Scheduled, label: 'Scheduled' },
    ])
    .sort();
};

export const isValidDate = (date: Nullable<Date> | string): boolean => {
  if (date == null) {
    return false;
  }

  const parsedDate = typeof date === 'string' ? new Date(date) : date;
  return isValid(parsedDate);
};

export const mapCriticalResultStatusToNumber = (status: CriticalResultStatus) => {
  switch (status) {
    case CriticalResultStatus.NotCritical:
      return '0';
    case CriticalResultStatus.CriticalAcknowledged:
      return '1';
    case CriticalResultStatus.Critical:
      return '2';
    default:
      return null;
  }
};

/**
 * Applies default values for toggle filters that have a defaultValue of 'true'
 * and are not already present in the filters object.
 *
 * This is used to ensure that toggle filters with a default value are properly
 * initialized when loading filters from various sources (URL, saved preferences, etc.)
 */
export const applyToggleDefaults = (
  filters: FilterStateType,
  availableFilters: FilterDefinitionType
): FilterStateType => {
  const result = { ...filters };
  Object.entries(availableFilters).forEach(([key, definition]) => {
    if (
      definition?.type === FilterVariant.Toggle &&
      definition?.defaultValue === 'true' &&
      result[key as WorklistFiltersType] == null
    ) {
      result[key as WorklistFiltersType] = 'true' as unknown as any;
    }
  });
  return result;
};

/**
 * Returns an array of filter keys that have been applied (have values).
 * This is used to determine which filters are currently active.
 *
 * @param filters The current filter state
 * @returns Array of filter keys that have values
 */
export const getAppliedFilters = (filters: FilterStateType): string[] => {
  return Object.entries(filters)
    .filter(([, value]) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value != null && value !== '';
    })
    .map(([key]) => key);
};

/**
 * Converts GraphQL FilterDefinitionInput array to FilterStateType
 * Used when reading filter preferences from GraphQL and applying them to the UI
 */
export const convertWorklistFiltersToState = (
  worklistFilters: readonly { name: string; values: readonly (string | null)[] }[]
): FilterStateType => {
  const filterState: FilterStateType = {};

  worklistFilters.forEach((filter) => {
    if (filter.name && filter.values) {
      const validValues = filter.values.filter((v): v is string => v != null);
      if (validValues.length > 0) {
        filterState[filter.name as keyof FilterStateType] = validValues;
      }
    }
  });

  return filterState;
};

/**
 * Converts FilterStateType to GraphQL FilterDefinitionInput array
 * Used when saving filter preferences from the UI to GraphQL
 * Filters out placeholder keys (starting with _, such as _customWorklistFiltersInFlight) and empty values
 */
export const convertStateToWorklistFilters = (
  filterState: FilterStateType
): Array<{ name: string; values: string[] }> => {
  return Object.entries(filterState)
    .filter(([name, values]) => {
      if (name.startsWith('_')) return false;
      if (!values || (Array.isArray(values) && values.length === 0)) return false;
      return true;
    })
    .map(([name, values]) => ({
      name,
      values: Array.isArray(values) ? [...values] : [values],
    }));
};
